import { deleteData, getData, post } from 'services/api';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { IdParamsType } from 'utils/types';
import { ResponsesData, SortField, SortOrder } from '../types';
import { getApiErrorMessage, throwIfRefused } from './apiError';
import { postToast } from 'features/common/utils/postFlash';
import { queryClient } from '../../../index';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useToastNotification } from 'hooks/useToastNotification';

export const useEventCustomFields = (isAttendeesList: boolean) => {
	const { id } = useParams<IdParamsType>();

	const [searchText, setSearchText] = useState('');
	const [pageParam, setPageParam] = useState(1);
	const [resultsPerPage, setResultsPerPage] = useState(30);
	const [sortBy, setSortBy] = useState<SortField | null>(null);
	const [order, setOrder] = useState<SortOrder>('asc');

	const responsesQueryObject = useInfiniteQuery({
		queryKey: ['responses', id, searchText, isAttendeesList, sortBy, order],
		queryFn: ({ pageParam = 1 }): Promise<ResponsesData[]> => {
			let url = `events/${id}/custom-fields/responses?page=${pageParam}&per_page=${resultsPerPage}&search=${searchText}&all_attendees=${isAttendeesList}`;

			if (sortBy) {
				url += `&sort_by=${sortBy}&order=${order}`;
			}

			return getData(url);
		},
		getNextPageParam: (lastPage, pages) => (lastPage.length >= resultsPerPage ? pages.length + 1 : undefined),
	});

	const handleSort = (field: SortField) => {
		if (sortBy === field) {
			// Toggle order if same field
			setOrder(order === 'asc' ? 'desc' : 'asc');
		} else {
			// New field, default to ascending
			setSortBy(field);
			setOrder('asc');
		}
	};

	return {
		searchText,
		setSearchText,
		pageParam,
		setPageParam,
		resultsPerPage,
		setResultsPerPage,
		responsesQueryObject,
		sortBy,
		order,
		handleSort,
	};
};

export const useResendEmail = (options?: { onSuccess?: () => void }) => {
	const { showNotification, TYPES } = useToastNotification();

	const { mutate: resendEmail, isLoading } = useMutation({
		mutationFn: (orderId: number) => post(`/public/ticketing/transactions/${orderId}/send-email`),
		onSuccess: async () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Email sent successfully' });
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
			if (options && options.onSuccess) options.onSuccess();
		},
		onError: (error: any) => {
			showNotification({
				type: TYPES.ERROR,
				details: 'The email could not be sent. Please check the ticket status and try again.',
				error,
			});
		},
	});

	return { resendEmail, isLoading };
};

export const useTransferTicket = (options?: { onSuccess?: () => void }) => {
	const { showNotification, TYPES } = useToastNotification();

	const { mutate: transferTicket, isLoading } = useMutation({
		mutationFn: ({ ticketId, recipientEmail }: { ticketId: number; recipientEmail: string }) =>
			post('/tickets/admin-transfer', {
				ticket_ids: [ticketId],
				recipient_email: recipientEmail,
			}),
		onSuccess: async () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Ticket transferred successfully' });
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
			if (options && options.onSuccess) options.onSuccess();
		},
		onError: (error: any) => {
			showNotification({
				type: TYPES.ERROR,
				details: 'The ticket could not be transferred. Please check the email address and ticket status.',
				error,
			});
		},
	});

	return { transferTicket, isLoading };
};

export const useTicketHistory = (ticketId?: number) => {
	return useQuery({
		queryKey: ['ticket-history', ticketId],
		queryFn: () => (ticketId ? getData(`/tickets/${ticketId}/history`) : Promise.resolve(null)),
		enabled: !!ticketId,
	});
};

export const useCancelTicket = (options?: { onSuccess?: () => void }) => {
	const { showNotification, TYPES } = useToastNotification();

	const { mutate: cancelTicket, isLoading } = useMutation({
		mutationFn: (ticketId: number) => deleteData(`/tickets/cancel-ticket/${ticketId}`),
		onSuccess: async () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Ticket canceled successfully' });
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
			if (options && options.onSuccess) options.onSuccess();
		},
		onError: (error: any) => {
			showNotification({
				type: TYPES.ERROR,
				details: 'The ticket could not be canceled. Please check the ticket status.',
				error,
			});
		},
	});
	return { cancelTicket, isLoading };
};

export const useUncancelTicket = (options?: { onSuccess?: () => void }) => {
	const { showNotification, TYPES } = useToastNotification();

	const { mutate: uncancelTicket, isLoading } = useMutation({
		mutationFn: (ticketId: number) => post(`/tickets/uncancel-ticket/${ticketId}`),
		onSuccess: async () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Ticket un-canceled successfully' });
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
			if (options && options.onSuccess) options.onSuccess();
		},
		onError: (error: any) => {
			showNotification({
				type: TYPES.ERROR,
				details: 'The ticket could not be un-canceled. Please check the ticket status.',
				error,
			});
		},
	});
	return { uncancelTicket, isLoading };
};

export const useRefundTicket = (options?: { onSuccess?: () => void }) => {
	const { showNotification, TYPES } = useToastNotification();

	const { mutate: refundTicket, isLoading } = useMutation({
		// A refusal arrives in the 207 body's errors[]; throwIfRefused routes it to onError.
		mutationFn: ({ ticketId, amount, description }: { ticketId: number; amount: number; description?: string }) =>
			post('/tickets/refund', {
				tickets: [{ id: ticketId, amount }],
				description,
			}).then(throwIfRefused),
		onSuccess: async () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Ticket refunded successfully' });
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
			if (options && options.onSuccess) options.onSuccess();
		},
		onError: (error: unknown) => {
			// `details` always wins over `error` in showNotification, so the API's reason must travel as details.
			showNotification({
				type: TYPES.ERROR,
				details: getApiErrorMessage(
					error,
					'The ticket could not be refunded. Please check the ticket status and amount.'
				),
			});
		},
	});
	return { refundTicket, isLoading };
};

export const useValidateTicket = (options?: { onSettled?: () => void }) => {
	const { mutate: validateTicket, isLoading } = useMutation({
		mutationFn: ({ ticketId, eventId }: { ticketId: number; eventId: string | number }) =>
			post('/tickets/validate', {
				ticket_ids: [String(ticketId)],
				event_id: String(eventId),
			}),
		onSuccess: async (response: any) => {
			const errors = response?.errors ?? [];
			if (Array.isArray(errors) && errors.length > 0) {
				// The refusal's reason sits under the API's capitalised `Error` key.
				const message = errors[0]?.error?.Error ?? 'Ticket could not be validated';
				postToast('error', message);
				return;
			}
			postToast('success', 'Ticket validated successfully');
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
		},
		onError: () => {
			postToast('error', 'Ticket could not be validated', 'Please check the ticket status.');
		},
		onSettled: () => {
			options?.onSettled?.();
		},
	});

	return { validateTicket, isLoading };
};

/**
 * Clears a check-in so the ticket scans again. Admin/editor only: gate the caller on `canUndoCheckIn`, since any
 * other role gets a 401 that the api client reads as an expired session. For a pass, `eventId` is the day whose
 * check-in is cleared; the API answers 409 when the ticket is not checked in for that event.
 */
export const useUndoCheckIn = (options?: { onSuccess?: () => void }) => {
	const { mutate: undoCheckIn, isLoading } = useMutation({
		mutationFn: ({ ticketId, eventId }: { ticketId: number; eventId: string | number }) =>
			post(`/tickets/${ticketId}/undo-check-in`, { event_id: Number(eventId) }),
		onSuccess: async () => {
			postToast('success', 'Check-in undone. The ticket can be scanned again.');
			await queryClient.invalidateQueries({ queryKey: ['responses'] });
			options?.onSuccess?.();
		},
		onError: (error: unknown) => {
			postToast('error', getApiErrorMessage(error, 'The check-in could not be undone.'));
		},
	});

	return { undoCheckIn, isLoading };
};

export const useTicketDetail = (ticketId?: number) =>
	useQuery({
		queryKey: ['ticket-detail', ticketId],
		queryFn: () => (ticketId ? getData(`/tickets/${ticketId}`) : Promise.resolve(null)),
		enabled: !!ticketId,
	});
